import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const REASONS = ["Ferie", "Malattia", "Formazione", "Permesso"];
const fmtSlots = (slots) => (slots?.length ? slots.map((s) => `${s.start}–${s.end}`).join(", ") : "Non disponibile");

export function AvailabilityDialog({ open, onOpenChange, target, override, standardSlots, onSaved }) {
  const [mode, setMode] = useState("standard");
  const [slots, setSlots] = useState([]);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setReason(override?.reason || "");
    if (!override) { setMode("standard"); setSlots(standardSlots?.length ? standardSlots : [{ start: "09:00", end: "13:00" }]); }
    else if (!override.slots.length) { setMode("off"); setSlots(standardSlots?.length ? standardSlots : [{ start: "09:00", end: "13:00" }]); }
    else { setMode("custom"); setSlots(override.slots); }
  }, [open, override, standardSlots]);

  if (!target) return null;
  const { user, dayIso } = target;
  const url = `/availability-overrides/${user.id}/${dayIso}`;
  const dayLabel = new Date(`${dayIso}T12:00:00`).toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long" });

  const updateSlot = (i, k, v) => setSlots((l) => l.map((s, j) => (j === i ? { ...s, [k]: v } : s)));

  const save = async () => {
    setSaving(true);
    try {
      if (mode === "standard") await api.delete(url);
      else await api.put(url, { slots: mode === "off" ? [] : slots, reason: reason.trim() });
      toast.success("Disponibilità aggiornata");
      onSaved();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="availability-dialog">
        <DialogHeader>
          <DialogTitle>Disponibilità del giorno</DialogTitle>
          <DialogDescription className="capitalize">{user.name} · {dayLabel}</DialogDescription>
        </DialogHeader>

        <RadioGroup value={mode} onValueChange={setMode} className="space-y-2">
          <label className="flex items-start gap-3 cursor-pointer">
            <RadioGroupItem value="standard" data-testid="availability-mode-standard" className="mt-0.5" />
            <div>
              <div className="text-sm font-medium">Orario standard</div>
              <div className="text-xs text-muted-foreground">{fmtSlots(standardSlots)}</div>
            </div>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <RadioGroupItem value="off" data-testid="availability-mode-off" />
            <span className="text-sm font-medium">Non disponibile tutto il giorno</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <RadioGroupItem value="custom" data-testid="availability-mode-custom" />
            <span className="text-sm font-medium">Orario personalizzato</span>
          </label>
        </RadioGroup>

        {mode === "custom" && (
          <div className="space-y-2 pl-7">
            {slots.map((s, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input type="time" value={s.start} onChange={(e) => updateSlot(i, "start", e.target.value)} className="w-28" data-testid={`availability-slot-start-${i}`} />
                <span className="text-muted-foreground">–</span>
                <Input type="time" value={s.end} onChange={(e) => updateSlot(i, "end", e.target.value)} className="w-28" data-testid={`availability-slot-end-${i}`} />
                <Button variant="ghost" size="icon" onClick={() => setSlots((l) => l.filter((_, j) => j !== i))} data-testid={`availability-slot-remove-${i}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setSlots((l) => [...l, { start: "15:00", end: "19:00" }])} data-testid="availability-slot-add">
              <Plus className="h-4 w-4 mr-1" /> Aggiungi fascia
            </Button>
            <Label className="block text-xs text-muted-foreground font-normal">Le ore fuori dalle fasce vengono mostrate in grigio.</Label>
          </div>
        )}

        {mode !== "standard" && (
          <div className="space-y-2" data-testid="availability-reason-section">
            <Label htmlFor="availability-reason">Motivo (facoltativo)</Label>
            <div className="flex flex-wrap gap-1.5">
              {REASONS.map((r) => (
                <Button key={r} type="button" size="sm" variant={reason === r ? "default" : "outline"}
                        onClick={() => setReason(reason === r ? "" : r)} data-testid={`availability-reason-${r.toLowerCase()}`}>
                  {r}
                </Button>
              ))}
            </div>
            <Input id="availability-reason" value={reason} maxLength={40} placeholder="Oppure scrivi un motivo"
                   onChange={(e) => setReason(e.target.value)} data-testid="availability-reason-input" />
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="availability-save-button">Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
